import type { AutosaveTriggerName } from "@redchain/contracts";
import type { SessionHeader } from "@redchain/input-log";
import wasmUrl from "@emu-dist/sameboy-core.wasm?url";
import {
  FAST_FORWARD_MAX,
  type AutosaveMessage,
  type LinkArmMessage,
  type LinkConnectMessage,
  type LinkStatus,
  type MainToWorker,
  type WorkerToMain,
} from "../worker/protocol";
import { AudioOut } from "./AudioOut";
import { InputManager } from "./InputManager";
import { Renderer } from "./Renderer";
import type { SessionClient } from "./SessionClient";

/** SHA-256 of the client core WASM: the core build hash sent at session start (§8.1). */
export async function computeCoreBuildHash(): Promise<string> {
  const res = await fetch(wasmUrl);
  if (!res.ok) throw new Error(`Couldn't load the emulator core (${res.status})`);
  const digest = await crypto.subtle.digest("SHA-256", await res.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface EmulatorHandlers {
  onReady?: () => void;
  onAutosave?: (m: AutosaveMessage) => void;
  onPurseApplied?: (m: Extract<WorkerToMain, { type: "purseApplied" }>) => void;
  onWalletOpen?: () => void;
  onStopped?: () => void;
  onError?: (message: string) => void;
}

/**
 * Main-thread side of the emulator: owns the worker, renderer, audio and input, and
 * hands worker chunks and checkpoints to the SessionClient (write-before-upload).
 */
export class EmulatorHost {
  readonly renderer: Renderer;
  readonly audio = new AudioOut();
  readonly input: InputManager;
  private readonly worker: Worker;
  private disposed = false;
  /** Link status from the worker (WEB-20); the LinkController subscribes. */
  onLink: (status: LinkStatus) => void = () => {};

  constructor(
    canvas: HTMLCanvasElement,
    container: HTMLElement,
    private readonly session: SessionClient,
    private readonly handlers: EmulatorHandlers,
  ) {
    this.renderer = new Renderer(canvas, container);
    this.worker = new Worker(new URL("../worker/emu.worker.ts", import.meta.url), { type: "module", name: "redchain-emu" });
    this.worker.onmessage = (ev: MessageEvent<WorkerToMain>) => this.onWorkerMessage(ev.data);
    this.worker.onerror = (ev) => handlers.onError?.(ev.message || "Emulator worker crashed");
    this.input = new InputManager(
      (mask) => this.send({ type: "mask", mask }),
      (on) => this.send({ type: "fastForward", on, maxSpeed: FAST_FORWARD_MAX }),
    );
    this.audio.onLevel = (bufferedFrames) => this.send({ type: "level", bufferedFrames });
  }

  async start(args: { rom: Uint8Array; sram: Uint8Array; sessionHeader: SessionHeader; autosaveTriggers: AutosaveTriggerName[] }): Promise<void> {
    await this.audio.start();
    const rom = args.rom.slice();
    const sram = args.sram.slice();
    this.send({ type: "init", rom, sram, sessionHeader: args.sessionHeader, autosaveTriggers: args.autosaveTriggers, startFrame: 0n }, [rom.buffer, sram.buffer]);
    this.input.attach();
    document.addEventListener("visibilitychange", this.onVisibility);
    if (document.visibilityState === "hidden") this.send({ type: "pause" });
  }

  powerCycle(): void {
    this.send({ type: "powerCycle" });
  }

  /** Stop feeding joypad masks while the Gen1 Wallet UI is open (core is already held). */
  openWalletUi(): void {
    this.input.detach();
  }

  /** Clear wRedChainWalletReq and resume stepping after the Wallet UI closes. */
  closeWalletUi(): void {
    this.send({ type: "walletClose" });
    this.input.attach();
  }

  purseCredit(creditId: string, amount: number): void {
    this.send({ type: "purseCredit", creditId, amount });
  }

  linkArm(m: Omit<LinkArmMessage, "type">): void {
    this.send({ type: "linkArm", ...m });
  }

  linkConnect(m: Omit<LinkConnectMessage, "type">): void {
    const transfer = m.opponentRom ? [m.opponentRom.buffer as ArrayBuffer] : [];
    this.send({ type: "linkConnect", ...m }, transfer);
  }

  linkCancel(): void {
    this.send({ type: "linkCancel" });
  }

  /** Session opened elsewhere: stop stepping and flush (§10.1). */
  superseded(): void {
    this.send({ type: "superseded" });
    this.input.detach();
  }

  private send(msg: MainToWorker, transfer: Transferable[] = []): void {
    if (!this.disposed) this.worker.postMessage(msg, transfer);
  }

  private onVisibility = (): void => {
    this.send({ type: document.visibilityState === "hidden" ? "pause" : "resume" });
  };

  private onWorkerMessage(msg: WorkerToMain): void {
    switch (msg.type) {
      case "frame":
        this.renderer.draw(msg.pixels);
        break;
      case "audio":
        this.audio.push(msg.samples);
        break;
      case "chunk":
        void this.session.enqueueChunk(msg);
        break;
      case "checkpoint":
        void this.session.enqueueCheckpoint(msg.meta, msg.sram);
        break;
      case "autosave":
        this.handlers.onAutosave?.(msg);
        break;
      case "purseApplied":
        this.handlers.onPurseApplied?.(msg);
        break;
      case "walletOpen":
        this.handlers.onWalletOpen?.();
        break;
      case "ready":
        this.handlers.onReady?.();
        break;
      case "stopped":
        this.handlers.onStopped?.();
        break;
      case "link":
        this.onLink(msg.status);
        break;
      case "error":
        this.handlers.onError?.(msg.message);
        break;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.send({ type: "pause" });
    this.disposed = true;
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.input.detach();
    this.renderer.dispose();
    void this.audio.stop();
    // Give the worker a moment to post its final chunk before terminating.
    setTimeout(() => this.worker.terminate(), 500);
  }
}
